import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import Sidebar from './Sidebar';
import config from '../config';
import PromoModal from './PromoModal';

const Promos = () => {
  const [promos, setPromos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [totalPromos, setTotalPromos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPromo, setCurrentPromo] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  
  const promosPerPage = 10;

  const fetchPromos = useCallback(async () => {
    try {
      setLoading(true);
      const limit = promosPerPage;
      const offset = (currentPage - 1) * limit;
      
      const response = await axios.get(`${config.apiBaseUrl}/api/promo/promos`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        params: {
          limit,
          offset,
          search: searchText,
        },
      });
      
      setPromos(response.data.promos);
      setTotalPromos(response.data.totalCount);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching promos:', error);
      setLoading(false);
    }
  }, [currentPage, searchText]);

  useEffect(() => {
    fetchPromos();
  }, [fetchPromos]);

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1); // Reset to first page when searching
    fetchPromos();
  };

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= Math.ceil(totalPromos / promosPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  const openCreateModal = () => {
    setCurrentPromo(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const openEditModal = (promo) => {
    setCurrentPromo(promo);
    setModalMode('edit');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleCreatePromo = async (promoData) => {
    try {
      await axios.post(
        `${config.apiBaseUrl}/api/promo/promos`,
        promoData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      fetchPromos();
      closeModal();
    } catch (error) {
      console.error('Error creating promo:', error);
      alert(error.response?.data?.message || 'Error creating promo');
    }
  };

  const handleUpdatePromo = async (promoData) => {
    try {
      await axios.put(
        `${config.apiBaseUrl}/api/promo/promos/${currentPromo.pid}`,
        promoData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        }
      );
      fetchPromos();
      closeModal();
    } catch (error) {
      console.error('Error updating promo:', error);
      alert(error.response?.data?.message || 'Error updating promo');
    }
  };

  const handleDeletePromo = async (pid) => {
    if (window.confirm('Are you sure you want to delete this promo code?')) {
      try {
        await axios.delete(`${config.apiBaseUrl}/api/promo/promos/${pid}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
          }
        });
        fetchPromos();
      } catch (error) {
        console.error('Error deleting promo:', error);
        alert(error.response?.data?.message || 'Error deleting promo');
      }
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-800 text-white w-full">
      <Sidebar />
      <div className="p-6 flex-1">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">Promo Codes</h1>
          <button
            onClick={openCreateModal}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center"
          >
            <FiPlus className="mr-2" /> Create New Promo
          </button>
        </div>

        <div className="flex items-center mb-6 bg-gray-700 p-4 rounded-lg">
          <div className="relative flex-grow">
            <input
              type="text"
              value={searchText}
              onChange={handleSearch}
              className="w-full p-3 pl-10 rounded text-black"
              placeholder="Search promos by name or description..."
            />
            <FiSearch className="absolute left-3 top-3.5 text-gray-400" />
          </div>
          <button
            onClick={handleSearchSubmit}
            className="ml-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded"
          >
            Search
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white text-black rounded-lg overflow-hidden">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="py-3 px-4 border-b">Name</th>
                    <th className="py-3 px-4 border-b">Description</th>
                    <th className="py-3 px-4 border-b">Discount(₹)</th>
                    <th className="py-3 px-4 border-b">Status</th>
                    <th className="py-3 px-4 border-b">Created</th>
                    <th className="py-3 px-4 border-b">Last Updated</th>
                    <th className="py-3 px-4 border-b">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promos.length > 0 ? (
                    promos.map((promo) => (
                      <tr key={promo.pid} className="hover:bg-gray-100">
                        <td className="py-3 px-4 border-b">{promo.name}</td>
                        <td className="py-3 px-4 border-b">
                          <div className="truncate max-w-xs">{promo.description}</div>
                        </td>
                        <td className="py-3 px-4 border-b text-center">₹{promo.discount}</td>
                        <td className="py-3 px-4 border-b text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              promo.status
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {promo.status ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="py-3 px-4 border-b">
                          {new Date(promo.createdon).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 border-b">
                          {new Date(promo.updatedon).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 border-b">
                          <div className="flex space-x-2 justify-center">
                            <button
                              onClick={() => openEditModal(promo)}
                              className="bg-yellow-500 hover:bg-yellow-700 text-white p-2 rounded"
                              title="Edit"
                            >
                              <FiEdit2 />
                            </button>
                            <button
                              onClick={() => handleDeletePromo(promo.pid)}
                              className="bg-red-500 hover:bg-red-700 text-white p-2 rounded"
                              title="Delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="py-4 text-center">
                        No promo codes found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between items-center mt-6">
              <div>
                Showing {promos.length} of {totalPromos} promo codes
              </div>
              <div className="flex">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`font-bold py-2 px-4 rounded mr-2 ${
                    currentPage === 1
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gray-500 hover:bg-gray-700 text-white'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage * promosPerPage >= totalPromos}
                  className={`font-bold py-2 px-4 rounded ${
                    currentPage * promosPerPage >= totalPromos
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-gray-500 hover:bg-gray-700 text-white'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <PromoModal
          mode={modalMode}
          promo={currentPromo}
          onClose={closeModal}
          onSubmit={modalMode === 'create' ? handleCreatePromo : handleUpdatePromo}
        />
      )}

      <style jsx>{`
        .loader {
          border-top-color: #3498db;
          -webkit-animation: spinner 1.5s linear infinite;
          animation: spinner 1.5s linear infinite;
        }

        @-webkit-keyframes spinner {
          0% { -webkit-transform: rotate(0deg); }
          100% { -webkit-transform: rotate(360deg); }
        }

        @keyframes spinner {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Promos;